import {
  Stack,
  Typography,
  Divider,
  Paper,
  Alert,
  Box,
  Grid,
  Link,
  Tooltip,
  IconButton,
} from "@mui/material";
import { FindInPage as FindInPageIcon } from "@mui/icons-material";

import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import cn from "classnames";
import { parse } from "tldts";

import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import styles from "./styles.module.css";

import Loading from "../Loading";
import theme from "../../theme";
import { useTrueBizApi } from "../../api";

export function MCCSearchResultBody({ searchId }: { searchId: UUID }) {
  const api = useTrueBizApi();

  const [done, setDone] = useState(false);

  const query = useQuery({
    queryKey: ["getBusinessSearch", searchId],
    queryFn: () => {
      if (!searchId) return;

      return api.getBusinessSearch({ searchId });
    },
    enabled: searchId != null,
    refetchInterval: done ? undefined : 750,
  });

  const mccSearch = query.data;

  useEffect(() => {
    if (!query.data?.status) return;

    if (query.data.status !== "PROCESSING") {
      setDone(true);
    }
  }, [query.data?.status]);

  if (!mccSearch) {
    // this should not happen because we already warmed the cache
    throw new Error("Cache miss");
  }

  const searchParamPairs = useMemo(() => {
    if (!mccSearch.query) return [];

    return [
      ["Business name", mccSearch.query.business_name || "—"],
      ["Website", mccSearch.query.website || "—"],
      ["State/Province", mccSearch.query.state_province || "—"],
      ["Postal code", mccSearch.query.postal_code || "—"],
      ["Country", mccSearch.query.country || "—"],
    ];
  }, [mccSearch]);

  return (
    <Stack>
      <Typography variant="h6">MCC Discovery</Typography>
      <Divider />

      <Box marginBottom={3} marginTop={1}>
        {mccSearch.status === "PROCESSING" && (
          <Alert severity="info">
            We&rsquo;re still analyzing all of the data for this search. This
            page will refresh when your search completes. Please check back in a
            few minutes.
          </Alert>
        )}
      </Box>

      <Grid container columnSpacing={3}>
        <Grid item xs={7}>
          <AnimatePresence>
            {mccSearch.status === "PROCESSING" && (
              <motion.div
                key="loading-animation"
                exit={{
                  height: 0,
                  opacity: 0,
                }}
              >
                <Paper
                  sx={{
                    marginBottom: 3,
                    padding: 2,
                    height: "250px",
                  }}
                >
                  <Loading>
                    {mccSearch.status_description && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        key="status-description"
                        className={cn(styles.shimmering)}
                      >
                        {mccSearch.status_description}
                      </motion.div>
                    )}
                  </Loading>
                </Paper>
              </motion.div>
            )}
            {mccSearch.status === "ERROR" && (
              <motion.div key="error-message">
                <Typography color="error">
                  There was a problem executing your search. Please try again
                  shortly.
                </Typography>
              </motion.div>
            )}
            {mccSearch.status === "FINISHED" && (
              <motion.div key="result">
                {mccSearch.result && (
                  <Paper sx={{ padding: 2 }}>
                    <Typography variant="h4">
                      {mccSearch.result?.business_name || "Unknown business"}
                    </Typography>
                    {(mccSearch.result?.website ||
                      mccSearch.result?.business_address) && (
                      <Stack
                        direction="column"
                        gap="0.25em"
                        sx={{ color: theme.palette.grey[600] }}
                      >
                        {mccSearch.result?.business_address?.full_address && (
                          <Typography>
                            {mccSearch.result.business_address.full_address}
                          </Typography>
                        )}
                        {mccSearch.result?.website && (
                          <Stack direction="row">
                            <a
                              href={mccSearch.result.website}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {mccSearch.result.website}
                            </a>
                            <div
                              style={{
                                height: "1.5em",
                                lineHeight: 1,
                                overflow: "visible",
                                marginTop: "-0.3em",
                              }}
                            >
                              <Link
                                href={`${window.location.origin}/domain/${
                                  parse(mccSearch.result.website).domain
                                }?run=1`}
                                target="_blank"
                              >
                                <Tooltip
                                  title={
                                    "Run full web presence search on this domain in a new tab."
                                  }
                                  placement="right"
                                  arrow
                                >
                                  <IconButton>
                                    <FindInPageIcon
                                      color="primary"
                                      fontSize="small"
                                    />
                                  </IconButton>
                                </Tooltip>
                              </Link>
                            </div>
                          </Stack>
                        )}
                      </Stack>
                    )}
                    {mccSearch.result?.business_description && (
                      <Typography
                        sx={{
                          marginTop: 2,
                          marginBottom: 3,
                          fontSize: "95%",
                        }}
                      >
                        {mccSearch.result.business_description}
                      </Typography>
                    )}
                    <Box>
                      <Stack
                        sx={{
                          marginBottom: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            color: theme.palette.grey[700],
                            fontWeight: 600,
                            marginBottom: "0.25em",
                          }}
                        >
                          Primary MCC
                        </Typography>
                        <Typography>
                          {mccSearch.result?.primary_mcc ? (
                            <div>
                              <span
                                style={{
                                  border: `1px solid ${theme.palette.primary.main}`,
                                  borderRadius: "5px",
                                  fontSize: "85%",
                                  color: "white",
                                  backgroundColor: `${theme.palette.primary.main}`,
                                  paddingTop: "2px",
                                  paddingBottom: "2px",
                                  paddingLeft: "4px",
                                  paddingRight: "4px",
                                }}
                              >
                                <strong>
                                  {mccSearch.result?.primary_mcc?.code}
                                </strong>
                              </span>{" "}
                              <span>
                                {mccSearch.result?.primary_mcc?.description ||
                                  ""}
                              </span>
                            </div>
                          ) : (
                            <span>—</span>
                          )}
                        </Typography>
                      </Stack>
                      <Stack>
                        <Typography
                          sx={{
                            color: theme.palette.grey[700],
                            fontWeight: 600,
                            marginTop: "1em",
                          }}
                        >
                          Additional MCCs
                        </Typography>
                        <Typography>
                          {mccSearch.result?.additional_mccs?.length > 0 ? (
                            <ul style={{ paddingLeft: "1em", marginTop: 0 }}>
                              {mccSearch.result.additional_mccs
                                .filter((x) => !!x)
                                .map((mcc, i) => (
                                  <li key={mcc.code}>
                                    <strong>{mcc.code}</strong>{" "}
                                    {mcc.description}
                                  </li>
                                ))}
                            </ul>
                          ) : (
                            <span style={{ color: theme.palette.grey[600] }}>
                              —
                            </span>
                          )}
                        </Typography>
                      </Stack>
                    </Box>
                  </Paper>
                )}
                {!mccSearch.result && (
                  <Typography>
                    We couldn&rsquo; find any results for your search.
                  </Typography>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </Grid>
        <Grid item xs={5}>
          <Grid container columnSpacing={3}>
            <Grid item xs={12} mb={2}>
              <div
                style={{
                  border: "1px solid #ccc",
                  padding: "0.5em",
                  borderRadius: "5px",
                  background: "#fdfdfd",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    minHeight: "38px",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <strong style={{ color: "#555" }}>
                      Parameters for Search
                    </strong>
                  </div>
                </div>
                <hr
                  style={{
                    borderStyle: "solid",
                    borderColor: "#eee",
                  }}
                />
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gridRowGap: "0.5em",
                  }}
                >
                  {searchParamPairs.map(([k, v]) => (
                    <div key={k}>
                      <span style={{ color: "#777" }}>{k}</span>
                      <br />
                      <span
                        style={{
                          wordBreak: "break-word",
                        }}
                      >
                        <strong style={{ color: "#555" }}>{v}</strong>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Stack>
  );
}

export default function MCCSearchResult() {
  const api = useTrueBizApi();
  const params = useParams();
  const { searchId } = params;

  const query = useQuery({
    queryKey: ["getBusinessSearch", searchId],
    queryFn: () => {
      if (!searchId) return;

      return api.getBusinessSearch({ searchId });
    },
    enabled: searchId != null,
  });

  if (!searchId)
    throw new Error("No searchId in route, this should not happen.");

  if (query.data) {
    return <MCCSearchResultBody searchId={searchId} />;
  }

  return null;
}
